/*
 * mobile-variant-scroll.js
 *
 * On MOBILE only, smooth-scroll the page back to the product gallery when
 * the user changes EITHER the finishing-color variant OR the return-desk
 * variant. SIZE and other variant changes do NOT trigger the scroll —
 * scope was deliberately narrowed so a size tap doesn't yank the page on
 * every interaction (A, 2026-04-29 evening tweak after initial Round 5 ship).
 *
 * The page-level eligibility gate is "product has multi-value finishing-color
 * OR has return-desk option (with multiple values)". Reasoning: return-desk
 * is only present on executive desks, and on those, swapping orientation
 * meaningfully changes the gallery image (L vs R) — so the scroll-back UX
 * applies even when the executive desk has no finishing metaobjects. This
 * was extended from "multi-finishing only" after A noted some executive
 * desks lack metaobjects but should still scroll on return-desk change
 * (2026-04-29 late, after Round 6 polish round 2).
 *
 * Loads independently of finishing-display.js so it also covers the bail
 * branch (multi-color products whose primary_finish metaobjects are
 * incomplete — see product-finishing-display.liquid:169-175).
 *
 * Self-scoping at runtime:
 *   - Mobile guard: matchMedia('(max-width: 768px)'), re-checked at click time.
 *   - Eligibility guard: scans .product-header_option[data-option-name]
 *     groups for one that's finishing-related (>1 radios) OR exactly
 *     'return-desk' (>1 radios).
 *   - Trigger filter: only fires on changes inside option groups whose
 *     data-option-name is finishing-related OR exactly 'return-desk'.
 *   - In-view guard: skips the scroll if the gallery is already comfortably
 *     visible, so a tap near the top doesn't cause a glitchy bounce.
 */

(function () {
  'use strict';

  function isMobile() {
    return window.matchMedia('(max-width: 768px)').matches;
  }

  // Same finishing keyword list as product-finishing-display.liquid:46
  // (handleized form — data-option-name in the legacy block is | handleize'd).
  var FINISHING_OPTION_NAMES = [
    'finishing-color', 'finish-color', 'finishing',
    'primary-finishing', 'finish'
  ];

  function isMultiFinishingPDP() {
    var groups = document.querySelectorAll(
      '.product-header_option[data-option-name]'
    );
    for (var i = 0; i < groups.length; i++) {
      var name = (groups[i].getAttribute('data-option-name') || '')
        .toLowerCase().trim();
      if (FINISHING_OPTION_NAMES.indexOf(name) === -1) continue;
      var radios = groups[i].querySelectorAll(
        'input[type="radio"].product-variant-option'
      );
      if (radios.length > 1) return true;
    }
    return false;
  }

  // Image-radio options that should trigger the mobile scroll-back when
  // changed. Each is a Webflow-style radio-with-image picker where swapping
  // the value meaningfully changes the gallery: 'return-desk' for executive
  // desks (Left/Right), 'configuration' for workstations (1-Person Inline,
  // 4-Person Cluster, etc.), 'reception-configuration' for reception desks
  // (Left/Right; Base-only single-value products are auto-skipped because
  // the page-level gate requires radios.length > 1).
  var IMAGE_RADIO_OPTION_NAMES = ['return-desk', 'configuration', 'reception-configuration'];

  function hasImageRadioOption() {
    var groups = document.querySelectorAll(
      '.product-header_option[data-option-name]'
    );
    for (var i = 0; i < groups.length; i++) {
      var name = (groups[i].getAttribute('data-option-name') || '')
        .toLowerCase().trim();
      if (IMAGE_RADIO_OPTION_NAMES.indexOf(name) === -1) continue;
      var radios = groups[i].querySelectorAll(
        'input[type="radio"].product-variant-option'
      );
      if (radios.length > 1) return true;
    }
    return false;
  }

  // Trigger filter: scroll only when the changed input is in a finishing-color
  // option group OR a return-desk option group. The input's own attributes
  // don't carry the option-name, so we walk up to the closest .product-header_option
  // wrapper. If the input has no such wrapper (e.g. floating size dropdown
  // outside the option-list), closest() returns null and we don't scroll —
  // which is the desired conservative default.
  function isTriggerOption(input) {
    var group = input.closest('.product-header_option[data-option-name]');
    if (!group) return false;
    var name = (group.getAttribute('data-option-name') || '').toLowerCase().trim();
    if (FINISHING_OPTION_NAMES.indexOf(name) !== -1) return true;
    if (IMAGE_RADIO_OPTION_NAMES.indexOf(name) !== -1) return true;
    return false;
  }

  function scrollToGallery() {
    var gallery = document.querySelector('.slick-product-gallery-container');
    if (!gallery) return;
    var rect = gallery.getBoundingClientRect();
    // Already comfortably in view (top is in the upper third of viewport)? Skip.
    if (rect.top >= 0 && rect.top < window.innerHeight * 0.3) return;
    var y = rect.top + window.pageYOffset - 12; // 12px breathing room
    window.scrollTo({ top: y, behavior: 'smooth' });
  }

  function init() {
    // Eligibility: page must have multi-value finishing-color OR return-desk.
    // Either condition opens the gate; the per-change isTriggerOption() filter
    // still narrows which specific changes actually scroll.
    if (!isMultiFinishingPDP() && !hasImageRadioOption()) return;
    document.querySelectorAll('.product-variant-option').forEach(function (input) {
      input.addEventListener('change', function () {
        if (!isMobile()) return;
        if (!isTriggerOption(input)) return;
        scrollToGallery();
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
